import React, { Fragment } from 'react';
import { ElementState } from '../../renderer/ElementState';
import { VideoPreset } from './VideoPreset';
import { PropertyCaption } from './PropertyCaption';
import { VideoFitPropertySelect } from './VideoFitPropertySelect';
import { PropertyNumericalInput } from './PropertyNumericalInput';

import styled from 'styled-components';
import { ElementAnimationSettings } from './ElementAnimationSettings';
import { numericalInputStyles } from '../../styles/mainStyle';
import { GenericAnimationSettings } from './GenericAnimationSettings';
import FadeProducer from '../../fadeEffectProcessor/FadeProducer';
import { FIT_DEFAULT } from '../../config/videoSettings';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import SliderSelect from '../common/PlainSlider';
import ScaleIcon from '@src/svgs/ScaleIcon';
import UpDownIcon from '@src/svgs/UpDownIcon';
import { useUserIdentity, useStore } from '@src/stores-v2/StoreContext';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import useLayoutAwareVideoSettingsState from './useLayoutAwareVideoSettingsState';
import { AspectRatio } from '@src/types.ts/video';

interface VideoSettingsProps {
  activeElement: ElementState;
}

export const VideoSettings: React.FC<VideoSettingsProps> = (props) => {
  const videoCreator = useVideoCreatorStore();
  const { editorStore } = useStore();
  const userIdentity = useUserIdentity();
  const { enableStackedLayouts } = useFlagsCombination(
    userIdentity.currentRole,
  );
  const isBroll = !videoCreator.isOriginalVideoElement(
    props.activeElement.source,
  );
  const fadeProducer = new FadeProducer(videoCreator, props.activeElement);

  // Helper function to update video element properties using headless editor or fallback to legacy
  const updateVideoProperty = async (propertyUpdates: Record<string, any>) => {
    if (editorStore?.headlessEnabled && editorStore?.editor && props.activeElement?.source?.id) {
      // Use headless editor UpdateElementCommand
      const success = editorStore.editor.commandUpdateElement(
        props.activeElement.source.id,
        propertyUpdates
      );
      
      if (!success) {
        console.warn(`Failed to update video element properties:`, propertyUpdates);
        // Fallback to legacy method
        await videoCreator.applyVideoStateModifications(
          Object.fromEntries(
            Object.entries(propertyUpdates).map(([key, value]) => [
              `${props.activeElement?.source.id}.${key}`,
              value
            ])
          ),
          true,
          'changing video settings'
        );
      }
    } else {
      // Fallback to legacy method
      await videoCreator.applyVideoStateModifications(
        Object.fromEntries(
          Object.entries(propertyUpdates).map(([key, value]) => [
            `${props.activeElement?.source.id}.${key}`,
            value
          ])
        ),
        true,
        'changing video settings'
      );
    }
  };

  const {
    hiddenParentElement,
    isLayoutComposition,
    childVideoElement,
    displayElement,
    sourceUrl,
    xValue,
    yValue,
    modifyPropertyWithSlider: originalModifyPropertyWithSlider,
    handleChangeScale: originalHandleChangeScale,
  } = useLayoutAwareVideoSettingsState({ activeElement: props.activeElement });

  // Override slider functions to use headless editor
  const modifyPropertyWithSlider = async (propertyName: string, newValue: number) => {
    const propertyValue = `${newValue}%`;
    const elementToModify = childVideoElement || props.activeElement;
    
    if (propertyName === 'x') {
      // Set all related x-position values
      await updateVideoProperty({
        x_anchor: propertyValue,
        x: propertyValue,
        x_alignment: propertyValue,
      });
    } else if (propertyName === 'y') {
      // Set all related y-position values
      await updateVideoProperty({
        y_anchor: propertyValue,
        y: propertyValue,
        y_alignment: propertyValue,
      });
    } else {
      await updateVideoProperty({ [propertyName]: propertyValue });
    }
  };

  const handleChangeScale = async (newValue: number) => {
    const scale = `${newValue}%`;
    const elementToModify = childVideoElement || props.activeElement;
    
    await updateVideoProperty({
      width: scale,
      height: scale,
      x_scale: scale,
      y_scale: scale,
    });
  };

  if (!props.activeElement) {
    return null;
  }

  return (
    <Fragment>
      <VideoPreset activeElement={displayElement} url={sourceUrl} />

      <ElementAnimationSettings activeElement={props.activeElement} />

      <GroupContent>
        <Item>
          <PropertyCaption>Start</PropertyCaption>
          <PropertyNumericalInput
            activeElement={props.activeElement}
            propertyName="time"
            defaultValue="auto"
            unit="s"
            customStyles={numericalInputStyles}
            getInputValue={async (property: string, value: string) => {
              const newVideoOverlays = await fadeProducer.resetCrossfadeOnVideo(
                parseFloat(value),
                props.activeElement.duration,
              );
              
              // Apply fade overlays using legacy method (complex video overlay logic)
              if (Object.keys(newVideoOverlays).length > 0) {
                await videoCreator.applyVideoStateModifications(
                  newVideoOverlays,
                  true,
                  'changing video placement',
                );
              }
              
              // Update the main property using headless editor
              await updateVideoProperty({ [property]: value });
            }}
          />
        </Item>

        <Item>
          <PropertyCaption>Duration</PropertyCaption>
          <PropertyNumericalInput
            activeElement={props.activeElement}
            propertyName="duration"
            defaultValue="auto"
            unit="s"
            customStyles={numericalInputStyles}
            getInputValue={async (property: string, value: string) => {
              const newVideoOverlays = await fadeProducer.resetCrossfadeOnVideo(
                props.activeElement.source.time,
                parseFloat(value),
              );
              
              // Apply fade overlays using legacy method (complex video overlay logic)
              if (Object.keys(newVideoOverlays).length > 0) {
                await videoCreator.applyVideoStateModifications(
                  newVideoOverlays,
                  true,
                  'changing video placement',
                );
              }
              
              // Update the main property using headless editor
              await updateVideoProperty({ [property]: value });
            }}
          />
        </Item>

        {/* Layout/Fit dropdown - show all options */}
        <Item>
          <PropertyCaption>
            {isLayoutComposition ? 'Layout Type' : 'Fit'}
          </PropertyCaption>
          <VideoFitPropertySelect
            activeElement={
              isLayoutComposition && hiddenParentElement
                ? hiddenParentElement
                : props.activeElement
            }
            propertyName="fit"
            defaultValue={isLayoutComposition ? 'stacked-1-1' : FIT_DEFAULT}
            options={[
              { caption: 'Cover', value: 'cover' },
              { caption: 'Contain', value: 'contain' },
              { caption: 'Fill', value: 'fill' },
              ...(enableStackedLayouts &&
              videoCreator.currentVideo?.aspectRatio === AspectRatio.AR_9_16
                ? [
                    { caption: 'Stacked 1:1', value: 'stacked-1-1' },
                    { caption: 'Stacked 1:3', value: 'stacked-1-3' },
                  ]
                : []),
            ]}
          />
        </Item>

        {/* Add a second fit dropdown for the child video element when we're inside a composition */}
        {childVideoElement && (
          <Item>
            <PropertyCaption>Inner Video Fit</PropertyCaption>
            <VideoFitPropertySelect
              activeElement={childVideoElement}
              propertyName="fit"
              defaultValue={FIT_DEFAULT}
              options={[
                { caption: 'Cover', value: 'cover' },
                { caption: 'Contain', value: 'contain' },
                { caption: 'Fill', value: 'fill' },
              ]}
            />
          </Item>
        )}

        {isBroll && (
          <Item>
            <PropertyCaption>Volume</PropertyCaption>
            <PropertyNumericalInput
              activeElement={props.activeElement}
              propertyName="volume"
              defaultValue="100"
              unit="%"
              customStyles={numericalInputStyles}
              getInputValue={async (property: string, value: string) => {
                await updateVideoProperty({ [property]: value });
              }}
            />
          </Item>
        )}
      </GroupContent>

      <GenericAnimationSettings
        hideFadeAnimations
        activeElement={props.activeElement}
      />

      <GroupContent>
        <Item>
          <PropertyCaption>Audio Fade In</PropertyCaption>
          <PropertyNumericalInput
            activeElement={props.activeElement}
            propertyName="audio_fade_in"
            defaultValue="0"
            unit="s"
            customStyles={numericalInputStyles}
            getInputValue={async (property: string, value: string) => {
              await updateVideoProperty({ [property]: parseFloat(value) });
            }}
          />
        </Item>
        <Item>
          <PropertyCaption>Audio Fade Out</PropertyCaption>
          <PropertyNumericalInput
            activeElement={props.activeElement}
            propertyName="audio_fade_out"
            defaultValue="0"
            unit="s"
            customStyles={numericalInputStyles}
            getInputValue={async (property: string, value: string) => {
              await updateVideoProperty({ [property]: parseFloat(value) });
            }}
          />
        </Item>
      </GroupContent>
      <GroupContent>
        <Item>
          <PropertyCaption>X-Alignment</PropertyCaption>
          <SliderAndIcon>
            <UpDownIcon rotate={90} />
            <SliderSelect
              getValue={(newValue) => modifyPropertyWithSlider('x', newValue)}
              defaultValue={50}
              currValue={xValue}
              min={0}
              max={100}
            />
          </SliderAndIcon>
        </Item>
        <Item>
          <PropertyCaption>Y-Alignment</PropertyCaption>
          <SliderAndIcon>
            <UpDownIcon />
            <SliderSelect
              getValue={(newValue) => modifyPropertyWithSlider('y', newValue)}
              defaultValue={50}
              currValue={yValue}
              min={0}
              max={100}
            />
          </SliderAndIcon>
        </Item>
        <Item>
          <PropertyCaption>Scale</PropertyCaption>
          <SliderAndIcon>
            <ScaleIcon />
            <SliderSelect
              getValue={(newValue) => handleChangeScale(newValue)}
              defaultValue={100}
              currValue={(() => {
                const element = childVideoElement || props.activeElement;
                if (
                  element &&
                  element.source &&
                  element.source.width !== undefined
                ) {
                  const width = element.source.width.toString();
                  const widthVal = parseFloat(width);
                  return !isNaN(widthVal) ? widthVal : 100;
                }
                return 100;
              })()}
              min={100}
              max={300}
            />
          </SliderAndIcon>
        </Item>
      </GroupContent>
    </Fragment>
  );
};

const GroupContent = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 10px;
`;

const Item = styled.div``;

const SliderAndIcon = styled.div`
  display: flex;
  border: 1px solid #484848;
  border-radius: 8px;
  padding: 12px;
  gap: 5px;
  align-items: center;
  height: 15px;
`;
